import React, {useEffect, useState} from "react";
import {FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField} from "@mui/material";
import {ArrowBackIosNew} from "@mui/icons-material";
import {GridSaveAltIcon} from "@mui/x-data-grid";
import * as Yup from "yup";
import {useFormik} from "formik";
import {Button, Grid, Modal, ModalClose, Sheet, Stack, Typography} from "@mui/joy";
import {Experimental_CssVarsProvider as CssVarsProvider} from "@mui/material/styles";
import {Autocomplete} from "@mui/lab";
import IBudget from "../../../types/budget.type";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import moment, {Moment} from "moment";
import IUser from "../../../types/user.type";
import {BudgetConcept} from "../../../types/enumeration/BudgetConcept";
import {BudgetConceptNames} from "../../../types/enumeration/BudgetConceptNames";
import IProvider from "../../../types/provider.type";

interface ModalProps {
  isOpen: boolean,
  onSubmit: (data: IBudget) => void,
  onClose: () => void,
  budget: IBudget | undefined,
  users: IUser[],
  providers: IProvider[],
  readOnly?: boolean;
}

const BudgetModal: React.FC<ModalProps> = (props) => {
  const { isOpen, onClose, onSubmit, budget, users, providers, readOnly = false} = props;
  const [resources, setResources] = useState<Array<IUser>>([]);
  const [selectedProviders, setSelectedProviders] = useState<Array<IProvider>>([]);

  const initialValues: IBudget = {
    id: budget?.id,
    amount: 0,
    comments: "",
    concept: BudgetConcept.FOOD,
    start: null,
    end: null,
    description: "",
    days: 0,
    resources: [],
    providers: [],
  };

  const validationSchema = Yup.object({
    concept: Yup.string().required('El Concepto es requerido'),
    description: Yup.string().required('La Descripcion es requerida'),
    amount: Yup.number().min(0).required('El Monto es requerido'),
    resources: Yup.number().when('concept', (concept: any) => {
      if (concept[0] !== BudgetConcept.SUBCONTRACT) {
        return Yup.array().min(1, 'Al menos tiene que seleccionar un (1) recurso').required()
      }
      return Yup.array().notRequired()
    }),
    providers: Yup.number().when('concept', (concept: any) => {
      if (concept[0] === BudgetConcept.SUBCONTRACT) {
        return Yup.array().min(1, 'Al menos tiene que seleccionar un (1) subcontratista').required()
      }
      return Yup.array().notRequired()
    }),
    start: Yup.number().when('concept', (concept: any) => {
      if (concept[0] !== BudgetConcept.BONUS && concept[0] !== BudgetConcept.VARIOUS) {
        return Yup.date().required('La Fecha Desde es requerida')
      }
      return Yup.date().notRequired()
    }),
    end: Yup.number().when('concept', (concept: any) => {
      if (concept[0] !== BudgetConcept.BONUS && concept[0] !== BudgetConcept.VARIOUS) {
        return Yup.date().required('La Fecha Hasta es requerida')
      }
      return Yup.date().notRequired()
    }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit: onSubmit,
    validationSchema: validationSchema,
  });

  const handleChangeStart = (value: Moment | null) => {
    formik.setFieldValue('start', moment(value).format('YYYY-MM-DDThh:mm:ss'))
  };

  const handleChangeEnd = (value: Moment | null) => {
    formik.setFieldValue('end', moment(value).format('YYYY-MM-DDThh:mm:ss'))
  };

  const handleChangeConcept = (event: SelectChangeEvent) => {
    formik.setFieldValue('concept', event.target.value)
    if (event.target.value === BudgetConcept.BONUS || event.target.value === BudgetConcept.VARIOUS) {
      formik.setErrors({start: undefined, end: undefined })
      formik.setFieldValue('start', null)
      formik.setFieldValue('end', null)
    }
  };

  useEffect(() => {
    if (isOpen && budget?.id) {
      formik.setValues(budget);
      formik.setFieldValue('start', moment(budget.start))
      formik.setFieldValue('end', moment(budget.end))
      setResources(budget.resources);
      setSelectedProviders(budget.providers);
    } else {
      formik.resetForm();
      setResources([]);
    }
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Sheet
        variant="outlined"
        sx={{
          width: 730,
          borderRadius: 'md',
          p: 3,
          boxShadow: 'lg',
          maxHeight: 750,
          overflow: "scroll"
        }}
      >
        <ModalClose variant="plain" sx={{ m: 1 }} />
        <Typography level="h4" component="h1" sx={{ paddingLeft: 1 }}>
          {budget?.id ? "Editar Presupuesto" : "Nuevo Presupuesto"}
        </Typography>
        <form className="form" onSubmit={formik.handleSubmit}>
          <Grid
            container
            rowSpacing={2}
            columnSpacing={2}
            marginBlock={1}
            marginInline={0}
            sx={{ width: '100%' }}
          >
            <Grid xl={12} md={12} sm={12} xs={12}>
              <CssVarsProvider>
                <FormControl variant="filled" fullWidth>
                  <InputLabel id="concept-label" shrink>Concepto</InputLabel>
                  <Select
                    labelId="concept-label"
                    id="concept"
                    name="concept"
                    label="concept"
                    value={formik.values.concept}
                    onBlur={formik.handleBlur}
                    onChange={handleChangeConcept}
                    readOnly={readOnly}
                  >
                    {Object
                      .keys(BudgetConcept)
                      .map((key) => (
                        <MenuItem key={key} value={BudgetConcept[key as keyof typeof BudgetConcept]}>
                          {BudgetConceptNames[BudgetConcept[key as keyof typeof BudgetConcept]]}
                        </MenuItem>
                      ))}
                  </Select>
                  {formik.touched.concept && formik.errors.concept ? (
                    <span className="Error-alert">{formik.errors.concept}</span>
                  ) : null}
                </FormControl>
              </CssVarsProvider>
            </Grid>

            <Grid xl={6} md={6} sm={6} xs={6}>
              <CssVarsProvider>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    disabled={formik.values.concept === BudgetConcept.BONUS
                      || formik.values.concept === BudgetConcept.VARIOUS}
                    name="start"
                    label="Fecha Desde"
                    format="DD/MM/YYYY"
                    value={formik.values.start ? moment(formik.values.start) : null }
                    onChange={handleChangeStart}
                    readOnly={readOnly}
                    slotProps={{
                      textField: {
                        variant: 'filled',
                        fullWidth: true,
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        error: formik.touched.start && Boolean(formik.errors.start),
                        InputLabelProps: {
                          shrink:  true
                        }
                      }
                    }} />
                </LocalizationProvider>
                {formik.touched.start && formik.errors.start ? (
                  <span className="Error-alert">{formik.errors.start}</span>
                ) : null}
              </CssVarsProvider>
            </Grid>

            <Grid xl={6} md={6} sm={6} xs={6}>
              <CssVarsProvider>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    disabled={formik.values.concept === BudgetConcept.BONUS
                      || formik.values.concept === BudgetConcept.VARIOUS}
                    name="end"
                    label="Fecha Hasta"
                    format="DD/MM/YYYY"
                    value={formik.values.end ? moment(formik.values.end) : null }
                    onChange={handleChangeEnd}
                    readOnly={readOnly}
                    slotProps={{
                      textField: {
                        variant: 'filled',
                        fullWidth: true,
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        error: formik.touched.end && Boolean(formik.errors.end),
                        InputLabelProps: {
                          shrink:  true
                        }
                      }
                    }} />
                </LocalizationProvider>
                {formik.touched.start && formik.errors.end ? (
                  <span className="Error-alert">{formik.errors.end}</span>
                ) : null}
              </CssVarsProvider>
            </Grid>

            <Grid xl={12} md={12} sm={12} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="description"
                  label="Descripcion"
                  name="description"
                  variant="filled"
                  type={"text"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.description}
                  error={!!(formik.touched.description && formik.errors.description)}
                  slotProps={{ inputLabel: {shrink: true}, input: {readOnly: readOnly}}}
                />
                {formik.touched.description && formik.errors.description ? (
                  <span className="Error-alert">{formik.errors.description}</span>
                ) : null}
              </CssVarsProvider>
            </Grid>

            <Grid xl={6} md={6} sm={6} xs={6}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  disabled
                  id="quantity"
                  label="Cantidad de Dias"
                  name="quantity"
                  variant="filled"
                  type={"number"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.days}
                  error={!!(formik.touched.days && formik.errors.days)}
                  slotProps={{ inputLabel: {shrink: true}, input: {readOnly: readOnly}}}
                />
              </CssVarsProvider>
            </Grid>

            <Grid xl={6} md={6} sm={6} xs={6}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="amount"
                  label="Monto Total"
                  name="amount"
                  variant="filled"
                  type={"number"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.amount}
                  error={!!(formik.touched.amount && formik.errors.amount)}
                  slotProps={{ inputLabel: {shrink: true}, input: {readOnly: readOnly}}}
                />
                {formik.touched.amount && formik.errors.amount ? (
                  <span className="Error-alert">{formik.errors.amount}</span>
                ) : null}
              </CssVarsProvider>
            </Grid>

            {formik.values.concept !== BudgetConcept.SUBCONTRACT &&
              <Grid xl={12} md={12} sm={12} xs={12}>
                <CssVarsProvider>
                  <Autocomplete
                    id="resources"
                    disabled={users.length === 0}
                    getOptionLabel={(user) =>
                      `${user?.lastName || ''}, ${user?.name || ''}`
                    }
                    options={users}
                    value={resources}
                    onChange={(e, value) => {
                      console.log("Nuevo valor seleccionado:", value);
                      setResources(value);
                      formik.setFieldValue("resources", value);
                    }}
                    multiple
                    fullWidth
                    size="small"
                    readOnly={readOnly}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant={"filled"}
                        label="Recursos"
                        name="resources"
                        slotProps={{ inputLabel: {shrink: true}}}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={!!(formik.touched.resources && formik.errors.resources)}
                      />
                    )}
                  />
                  {formik.touched.resources && formik.errors.resources ? (
                    <span className="Error-alert">{String(formik.errors.resources)}</span>
                  ) : null}
                </CssVarsProvider>
              </Grid>
            }

            {formik.values.concept === BudgetConcept.SUBCONTRACT &&
              <Grid xl={12} md={12} sm={12} xs={12}>
                <CssVarsProvider>
                  <Autocomplete
                    id="providers"
                    getOptionLabel={(user) => user?.name }
                    options={providers}
                    value={selectedProviders}
                    onChange={(e, value) => {
                      setSelectedProviders(value);
                      formik.setFieldValue("providers", value);
                    }}
                    multiple
                    fullWidth
                    size="small"
                    readOnly={readOnly}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant={"filled"}
                        label="Subcontratistas"
                        name="providers"
                        slotProps={{ inputLabel: {shrink: true}}}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={!!(formik.touched.providers && formik.errors.providers)}
                      />
                    )}
                  />
                  {formik.touched.providers && formik.errors.providers ? (
                    <span className="Error-alert">{String(formik.errors.providers)}</span>
                  ) : null}
                </CssVarsProvider>
              </Grid>
            }

            <Grid xl={12} md={12} sm={12} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="comments"
                  label="Comentarios"
                  name="comments"
                  variant="filled"
                  type={"text"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.comments}
                  error={!!(formik.touched.comments && formik.errors.comments)}
                  slotProps={{ inputLabel: {shrink: true}, input: {readOnly: readOnly}}}
                  multiline
                  rows={3}
                />
                {formik.touched.comments && formik.errors.comments ? (
                  <span className="Error-alert">{formik.errors.comments}</span>
                ) : null}
              </CssVarsProvider>
            </Grid>

          </Grid>
          <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{paddingRight: 1}}>
            <Button variant="outlined" endDecorator={<ArrowBackIosNew />} onClick={onClose}>Cerrar</Button>
            {!readOnly &&
              <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon />}>Guardar</Button>
            }
          </Stack>
        </form>
      </Sheet>
    </Modal>
  );
};

export default BudgetModal;
